import { useState, useEffect } from "react";
import { admin, type AdminUser } from "@/lib/api";
import Spinner from "@/components/Spinner";
import { AlertTriangle, RotateCcw, TrendingUp } from "lucide-react";
import clsx from "clsx";

function UsageBar({ used, limit, color }: { used: number; limit: number; color: string }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div>
      <div className="h-2 bg-black/[0.04] rounded-full overflow-hidden mb-1">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-slate">
        {used.toLocaleString()} / {limit >= 999999 ? "∞" : limit.toLocaleString()}
        <span className={clsx("ml-1.5 font-semibold", pct >= 100 ? "text-red-500" : pct >= 80 ? "text-amber-600" : "text-slate/50")}>
          {pct}%
        </span>
      </p>
    </div>
  );
}

export default function AdminUsagePage() {
  const [users, setUsers]       = useState<AdminUser[]>([]);
  const [loading, setLoading]   = useState(true);
  const [resetting, setResetting] = useState<string | null>(null);
  const [filter, setFilter]     = useState<"all"|"warning"|"over">("all");

  const load = () => {
    setLoading(true);
    admin.usage().then((r) => setUsers(r.users)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleReset = async (userId: string) => {
    setResetting(userId);
    try { await admin.resetUsage(userId); load(); }
    catch (e: any) { alert(e.message || "Reset failed"); }
    finally { setResetting(null); }
  };

  const filtered = users.filter((u: any) => {
    const emailPct = u.monthly_email_limit > 0
      ? (u.emails_sent_this_month / u.monthly_email_limit) * 100 : 0;
    const subPct = u.subscriber_limit > 0
      ? (u.active_subscribers / u.subscriber_limit) * 100 : 0;
    const maxPct = Math.max(emailPct, subPct);
    if (filter === "over")    return maxPct >= 100;
    if (filter === "warning") return maxPct >= 80 && maxPct < 100;
    return true;
  });

  const over    = users.filter((u: any) => {
    const ep = u.monthly_email_limit > 0 ? (u.emails_sent_this_month / u.monthly_email_limit) * 100 : 0;
    const sp = u.subscriber_limit > 0 ? (u.active_subscribers / u.subscriber_limit) * 100 : 0;
    return Math.max(ep, sp) >= 100;
  }).length;

  const warning = users.filter((u: any) => {
    const ep = u.monthly_email_limit > 0 ? (u.emails_sent_this_month / u.monthly_email_limit) * 100 : 0;
    const sp = u.subscriber_limit > 0 ? (u.active_subscribers / u.subscriber_limit) * 100 : 0;
    const m = Math.max(ep, sp);
    return m >= 80 && m < 100;
  }).length;

  return (
    <div>
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-black/[0.06] px-4 sm:px-6 lg:px-8 py-4">
        <h1 className="text-lg sm:text-xl font-bold text-ink">Usage & Limits</h1>
        <p className="text-xs sm:text-sm text-slate mt-0.5">Plan usage across all accounts</p>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-5">

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          {[
            { label: "Over limit",  value: over,    color: "bg-red-50 text-red-600",   icon: AlertTriangle },
            { label: "At warning",  value: warning,  color: "bg-amber-50 text-amber-600",icon: AlertTriangle },
            { label: "Healthy",     value: users.length - over - warning, color: "bg-green-50 text-green-600", icon: TrendingUp },
          ].map(({ label, value, color, icon: Icon }) => (
            <div key={label} className="bg-white rounded-2xl border border-black/[0.06] p-4 sm:p-5">
              <div className={`inline-flex p-2 rounded-xl mb-3 ${color}`}><Icon size={15} /></div>
              <p className="text-2xl font-bold text-ink">{value}</p>
              <p className="text-xs text-slate font-semibold uppercase tracking-wider mt-1">{label}</p>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="flex gap-1.5 p-1 bg-black/[0.04] rounded-xl w-fit">
          {(["all", "warning", "over"] as const).map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={clsx("px-4 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all",
                filter === f ? "bg-white text-ink shadow-sm" : "text-slate hover:text-ink")}>
              {f === "all" ? "All users" : f === "warning" ? "At warning (80%+)" : "Over limit (100%+)"}
            </button>
          ))}
        </div>

        {loading ? <Spinner className="py-16" /> : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/[0.06] py-16 text-center">
            <TrendingUp size={28} className="mx-auto mb-3 opacity-20" />
            <p className="text-slate text-sm">No users match this filter.</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-black/[0.06]">
                    {["User", "Plan", "Subscribers", "Emails this month", ""].map((h) => (
                      <th key={h} className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u: any) => {
                    const subPct = u.subscriber_limit > 0 ? (u.active_subscribers / u.subscriber_limit) * 100 : 0;
                    const emailPct = u.monthly_email_limit > 0 ? (u.emails_sent_this_month / u.monthly_email_limit) * 100 : 0;
                    return (
                      <tr key={u.id} className={clsx("border-b border-black/[0.04]",
                        Math.max(subPct, emailPct) >= 100 ? "bg-red-50/30" :
                        Math.max(subPct, emailPct) >= 80  ? "bg-amber-50/30" : "")}>
                        <td className="px-5 py-4">
                          <p className="font-semibold text-ink">{u.name || "—"}</p>
                          <p className="text-xs text-slate">{u.email}</p>
                        </td>
                        <td className="px-5 py-4 text-sm capitalize text-slate font-medium">{u.plan}</td>
                        <td className="px-5 py-4 min-w-[180px]">
                          <UsageBar used={u.active_subscribers || 0} limit={u.subscriber_limit || 1000}
                            color={subPct >= 100 ? "bg-red-500" : subPct >= 80 ? "bg-amber-400" : "bg-teal"} />
                        </td>
                        <td className="px-5 py-4 min-w-[180px]">
                          <UsageBar used={u.emails_sent_this_month || 0} limit={u.monthly_email_limit || 15000}
                            color={emailPct >= 100 ? "bg-red-500" : emailPct >= 80 ? "bg-amber-400" : "bg-coral"} />
                        </td>
                        <td className="px-5 py-4">
                          <button onClick={() => handleReset(u.id)} disabled={resetting === u.id}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-black/[0.08] hover:border-black/[0.16] transition-colors disabled:opacity-50">
                            <RotateCcw size={12} className={resetting === u.id ? "animate-spin" : ""} />
                            Reset
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden space-y-3">
              {filtered.map((u: any) => {
                const subPct = u.subscriber_limit > 0 ? (u.active_subscribers / u.subscriber_limit) * 100 : 0;
                const emailPct = u.monthly_email_limit > 0 ? (u.emails_sent_this_month / u.monthly_email_limit) * 100 : 0;
                return (
                  <div key={u.id} className="bg-white rounded-2xl border border-black/[0.06] p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-ink text-sm">{u.name || u.email}</p>
                        <p className="text-xs text-slate capitalize">{u.plan} plan</p>
                      </div>
                      <button onClick={() => handleReset(u.id)} disabled={resetting === u.id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-black/[0.08] disabled:opacity-50">
                        <RotateCcw size={11} /> Reset
                      </button>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <p className="text-[10px] text-slate font-semibold uppercase tracking-wide mb-1">Subscribers</p>
                        <UsageBar used={u.active_subscribers || 0} limit={u.subscriber_limit || 1000}
                          color={subPct >= 100 ? "bg-red-500" : subPct >= 80 ? "bg-amber-400" : "bg-teal"} />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate font-semibold uppercase tracking-wide mb-1">Emails</p>
                        <UsageBar used={u.emails_sent_this_month || 0} limit={u.monthly_email_limit || 15000}
                          color={emailPct >= 100 ? "bg-red-500" : emailPct >= 80 ? "bg-amber-400" : "bg-coral"} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
